import * as React from 'react';

import { PageHeader } from '../../components/core/PageHeader';
import {
  ChoiceGroup,
  IChoiceGroupOption,
  TextField,
  Stack,
  DatePicker,
  PrimaryButton,
  Dropdown,
  IDropdownOption,
  Label,
  MessageBar,
  MessageBarType,
  Spinner,
  DefaultButton,
  Icon,
} from '@fluentui/react';
import moment from 'moment';
import { HealAPI } from '../../healApi';
import Papa from 'papaparse';

declare const healConfig: any;

export interface PlansProps {}

export interface PlansState {
  saving?: boolean;
  error?: string;
  ingestionType?: string;
  single: {
    source?: string;
    dob?: Date;
    firstName?: string;
    lastName?: string;
    memberId?: string;
  };
  list: {
    data?: any[];
    mappingOptions: IDropdownOption[];
    source?: string;
    dobMappedFrom?: string;
    firstNameMappedFrom?: string;
    lastNameMappedFrom?: string;
    memberIdMappedFrom?: string;
  };
}

const planSourceOptions: IDropdownOption[] = [
  { key: 'VEBA_MEMBERS', text: 'VEBA Member' },
  { key: 'BSC_CALPERS', text: 'BSC CalPERS Member' },
  { key: 'BSC_MENTAL_HEALTH', text: 'BSC Mental Health' },
  { key: 'BSC_HRA_2021', text: 'BSC HRA 2021' },
];

export class Plans extends React.Component<PlansProps, PlansState> {
  constructor(props: PlansProps) {
    super(props);

    this.state = {
      ingestionType: 'member',
      single: {
        source: 'VEBA_MEMBERS',
      },
      list: {
        source: 'VEBA_MEMBERS',
        mappingOptions: [],
      },
    };
  }

  public render(): JSX.Element {
    return (
      <div>
        <PageHeader title="Plans" />
        {this.renderIngestionType()}
        {this.renderIngestionForm()}
        {this.renderIngestionBulk()}
      </div>
    );
  }

  private renderIngestionType(): JSX.Element {
    const options: IChoiceGroupOption[] = [
      { key: 'member', text: 'Single', iconProps: { iconName: 'AddFriend' } },
      { key: 'list', text: 'List', iconProps: { iconName: 'AddGroup' } },
    ];

    return <ChoiceGroup label="What are you adding?" selectedKey={this.state.ingestionType} onChange={(ev, opt) => this.setState({ ingestionType: opt!.key })} options={options} />;
  }

  private renderIngestionForm(): JSX.Element | null {
    if (this.state.ingestionType !== 'member') {
      return null;
    }

    return (
      <>
        <Stack horizontal tokens={{ childrenGap: 20 }}>
          <Stack tokens={{ childrenGap: 8 }} styles={{ root: { width: 400, marginBottom: 20 } }}>
            <Dropdown
              label="Source"
              placeholder="Select member type"
              required={true}
              options={planSourceOptions}
              selectedKey={this.state.single.source}
              onChange={(a, v) => this.setState({ ...this.state, single: { ...this.state.single, source: v!.key as string } })}
            />
            <TextField
              required={true}
              label="First Name"
              value={this.state.single.firstName}
              onChange={(a, v) => this.setState({ ...this.state, single: { ...this.state.single, firstName: v } })}
            />
            <TextField
              required={true}
              label="Last Name"
              value={this.state.single.lastName}
              onChange={(a, v) => this.setState({ ...this.state, single: { ...this.state.single, lastName: v } })}
            />
            <DatePicker
              onSelectDate={(date: Date | null | undefined) => date && this.setState({ ...this.state, single: { ...this.state.single, dob: date } })}
              label="Date of Birth (m/d/yyyy)"
              value={this.state.single.dob ? new Date(this.state.single.dob) : undefined}
              isRequired={true}
              allowTextInput={true}
              disableAutoFocus={true}
              formatDate={(date) => (date ? date.getMonth() + 1 + '/' + date.getDate() + '/' + date.getFullYear() : '')}
            />
            <TextField
              required={true}
              label="Member ID"
              value={this.state.single.memberId}
              onChange={(a, v) => this.setState({ ...this.state, single: { ...this.state.single, memberId: v } })}
            />
          </Stack>
          <Stack>
            <Label>Diagnostics</Label>
            <pre style={{ overflow: 'auto', height: 500, width: 700, padding: 12, background: '#eaeaea' }}>{JSON.stringify(this.generatePlan(), null, 2)}</pre>
          </Stack>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { width: 400, marginBottom: 20 } }}>
          <PrimaryButton
            text="Create"
            onClick={this.submit}
            disabled={this.state.saving || !this.state.single.dob || !this.state.single.firstName || !this.state.single.lastName || !this.state.single.memberId}
          />
          <DefaultButton text="Search" target="_blank" href={this.buildSearchLink()} />
          {this.state.saving && <Spinner />}
        </Stack>
        {this.state.error && <MessageBar messageBarType={MessageBarType.error}>{this.state.error}</MessageBar>}
      </>
    );
  }

  private renderIngestionBulk(): JSX.Element | null {
    if (this.state.ingestionType !== 'list') {
      return null;
    }

    return (
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { width: 400, marginTop: 20 } }}>
        <Dropdown
          label="Source"
          placeholder="Select member type"
          required={true}
          options={planSourceOptions}
          selectedKey={this.state.list.source}
          onChange={(a, v) => this.setState({ ...this.state, list: { ...this.state.list, source: v!.key as string } })}
        />
        <input value={undefined} type="file" accept="text/csv" onChange={this.onFileChange} />
        {this.state.list.mappingOptions.length > 0 && (
          <>
            <Stack horizontal styles={{ root: { alignItems: 'baseline', paddingTop: 10 } }}>
              <Label style={{ width: 110 }} required={true}>
                First Name
              </Label>
              <Icon iconName="ChevronLeft" style={{ marginLeft: 20, marginRight: 40 }} />
              <Dropdown
                placeholder="Select column to be mapped"
                options={this.state.list.mappingOptions}
                selectedKey={this.state.list.firstNameMappedFrom}
                onChange={(a, v) => this.setState({ ...this.state, list: { ...this.state.list, firstNameMappedFrom: v!.key as string } })}
              />
            </Stack>
            <Stack horizontal styles={{ root: { alignItems: 'baseline', paddingTop: 10 } }}>
              <Label style={{ width: 110 }} required={true}>
                Last Name
              </Label>
              <Icon iconName="ChevronLeft" style={{ marginLeft: 20, marginRight: 40 }} />
              <Dropdown
                placeholder="Select column to be mapped"
                options={this.state.list.mappingOptions}
                selectedKey={this.state.list.lastNameMappedFrom}
                onChange={(a, v) => this.setState({ ...this.state, list: { ...this.state.list, lastNameMappedFrom: v!.key as string } })}
              />
            </Stack>
            <Stack horizontal styles={{ root: { alignItems: 'baseline', paddingTop: 10 } }}>
              <Label style={{ width: 110 }} required={true}>
                Date of Birth
              </Label>
              <Icon iconName="ChevronLeft" style={{ marginLeft: 20, marginRight: 40 }} />
              <Dropdown
                placeholder="Select column to be mapped"
                options={this.state.list.mappingOptions}
                selectedKey={this.state.list.dobMappedFrom}
                onChange={(a, v) => this.setState({ ...this.state, list: { ...this.state.list, dobMappedFrom: v!.key as string } })}
              />
            </Stack>
            <Stack horizontal styles={{ root: { alignItems: 'baseline', paddingTop: 10, paddingBottom: 20 } }}>
              <Label style={{ width: 110 }} required={true}>
                Member ID
              </Label>
              <Icon iconName="ChevronLeft" style={{ marginLeft: 20, marginRight: 40 }} />
              <Dropdown
                placeholder="Select column to be mapped"
                options={this.state.list.mappingOptions}
                selectedKey={this.state.list.memberIdMappedFrom}
                onChange={(a, v) => this.setState({ ...this.state, list: { ...this.state.list, memberIdMappedFrom: v!.key as string } })}
              />
            </Stack>
            <PrimaryButton
              text="Generate JSON-list"
              onClick={this.generateJsonList}
              disabled={!this.state.list.firstNameMappedFrom || !this.state.list.lastNameMappedFrom || !this.state.list.dobMappedFrom || !this.state.list.memberIdMappedFrom}
            />
          </>
        )}
      </Stack>
    );
  }

  private onFileChange = (ev: React.SyntheticEvent<HTMLInputElement>) => {
    const files = ev.currentTarget.files;
    if (!files) {
      return;
    }

    Papa.parse(files[0], {
      skipEmptyLines: true,
      complete: (results) => {
        const headers: any = results.data.splice(0, 1)[0];
        const data = results.data;
        const mappingOptions = headers.map((h: any) => ({ key: h, text: h }));
        this.setState({ ...this.state, list: { ...this.state.list, mappingOptions, data } });
      },
    });
  };

  private submit = async () => {
    this.setState({ saving: true, error: undefined });
    const { firstName, lastName, memberId, dob, source } = this.state.single;

    const payerCode = source === 'VEBA_MEMBERS' ? '39026' : 'BS001';

    const plans = await HealAPI.findInsurancePlan(memberId!, firstName!, lastName!, moment(dob).format('YYYY-MM-DD'), payerCode);
    if (plans.data && plans.data.length !== 0) {
      this.setState({ saving: false, error: 'One or more plans already exist for this member.' });
    } else {
      await HealAPI.createInsurancePlan(this.generatePlan());
      this.setState({ saving: false });
    }
  };

  private generateJsonList = () => {
    const { data, mappingOptions, source, dobMappedFrom, firstNameMappedFrom, lastNameMappedFrom, memberIdMappedFrom } = this.state.list;

    const firstNameIndex = mappingOptions.findIndex((i) => i.key === firstNameMappedFrom);
    const lastNameIndex = mappingOptions.findIndex((i) => i.key === lastNameMappedFrom);
    const dobIndex = mappingOptions.findIndex((i) => i.key === dobMappedFrom);
    const memberIdIndex = mappingOptions.findIndex((i) => i.key === memberIdMappedFrom);
    const nowUtc = moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';

    const jsonList =
      data?.map((d) => {
        const firstName = d[firstNameIndex].trim();
        const lastName = d[lastNameIndex].trim();
        const memberId = d[memberIdIndex].trim();
        const dob = d[dobIndex].trim();
        if (source === 'VEBA_MEMBERS') {
          return JSON.stringify(this.generateVebaPlan(firstName, lastName, memberId, dob, source, nowUtc));
        } else if (source === 'BSC_CALPERS') {
          return JSON.stringify(this.generateBlueShieldCalpersPlan(firstName, lastName, memberId, dob, source, nowUtc));
        } else if (source === 'BSC_MENTAL_HEALTH') {
          return JSON.stringify(this.generateBlueShieldMentalHealthPlan(firstName, lastName, memberId, dob, source, nowUtc));
        } else if (source === 'BSC_HRA_2021') {
          return JSON.stringify(this.generateBlueShieldHRAPlan(firstName, lastName, memberId, dob, source, nowUtc));
        }
        return '';
      }) || [];

    download(`${source?.toLowerCase()}_${moment().format('MM_DD_YYYY')}.csv.json-list`, jsonList?.join('\n'));
  };

  private buildSearchLink(): string {
    const { firstName, lastName, memberId, dob, source } = this.state.single;
    if (source === 'VEBA_MEMBERS') {
      return `https://insuranceplanservice.${healConfig.env}-uswest2.heal.com/v1/insuranceplan/q?memberFirstName=${firstName}&memberLastName=${lastName}&memberDob=${moment(
        dob
      ).format('YYYY-MM-DD')}&memberId=${memberId}&payerCode=39026`;
    } else if (source === 'BSC_CALPERS' || source === 'BSC_MENTAL_HEALTH' || source === 'BSC_HRA_2021') {
      return `https://insuranceplanservice.${healConfig.env}-uswest2.heal.com/v1/insuranceplan/q?memberFirstName=${firstName}&memberLastName=${lastName}&memberDob=${moment(
        dob
      ).format('YYYY-MM-DD')}&memberId=${memberId}&payerCode=BS001`;
    }
    return '';
  }

  private generatePlan(): any {
    const { firstName, lastName, memberId, dob, source } = this.state.single;
    const nowUtc = moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';

    if (source === 'VEBA_MEMBERS') {
      return this.generateVebaPlan(firstName!, lastName!, memberId!, dob!, source!, nowUtc);
    } else if (source === 'BSC_CALPERS') {
      return this.generateBlueShieldCalpersPlan(firstName!, lastName!, memberId!, dob!, source!, nowUtc);
    } else if (source === 'BSC_MENTAL_HEALTH') {
      return this.generateBlueShieldMentalHealthPlan(firstName!, lastName!, memberId!, dob!, source!, nowUtc);
    } else if (source === 'BSC_HRA_2021') {
      return this.generateBlueShieldHRAPlan(firstName!, lastName!, memberId!, dob!, source!, nowUtc);
    }
    return {};
  }

  private generateVebaPlan(firstName: string, lastName: string, memberId: string, dob: Date, source: string, nowUtc: string): any {
    const plan = {
      subscriberDetails: {
        memberId: memberId,
        fragmentSource: source,
        fragmentUpdatedAt: nowUtc,
      },
      members: [
        {
          firstName: firstName,
          lastName: lastName,
          middleName: null,
          dateOfBirth: moment(dob).format('YYYY-MM-DD'),
          sex: null,
          relationshipToSubscriber: null,
          address: null,
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
        },
      ],
      payer: {
        id: '39026',
        name: 'UMR (United Healthcare)',
        address: null,
        code: '39026',
        fragmentUpdatedAt: nowUtc,
        fragmentSource: source,
      },
      planDetails: null,
      networkStatus: null,
      additionalBenefits: [
        {
          code: 'VEBA_FREE_VISITS',
          description: 'Free visits',
          type: 'VISIT_CREDIT',
          level: 'FAMILY',
          serviceConstraints: {
            mode: 'INCLUDE',
            criteriaList: [
              {
                code: 'SICK_CHILD',
                level: 'FAMILY',
              },
              {
                code: 'SICK_ADULT',
                level: 'FAMILY',
              },
              {
                code: 'ANPS_CHILD',
                level: 'FAMILY',
              },
              {
                code: 'ANPS_ADULT',
                level: 'FAMILY',
              },
              {
                code: 'TELEMED_ADULT',
                level: 'FAMILY',
              },
              {
                code: 'TELEMED_CHILD',
                level: 'FAMILY',
              },
            ],
            fragmentUpdatedAt: nowUtc,
            fragmentSource: source,
          },
          beginDate: null,
          endDate: null,
          credits: {
            total: 1000,
            remaining: 1000,
          },
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
          metadata: null,
        },
      ],
      reimbursement: null,
      tags: [
        {
          name: planSourceOptions.find((o) => o.key === source)?.text,
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
        },
      ],
      fragmentSources: [source],
    };

    return plan;
  }

  private generateBlueShieldCalpersPlan(firstName: string, lastName: string, memberId: string, dob: Date, source: string, nowUtc: string): any {
    const endUtc =
      moment(`01/01/${new Date().getFullYear() + 1}`)
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';

    const plan = {
      subscriberDetails: {
        memberId: memberId,
        fragmentSource: source,
        fragmentUpdatedAt: nowUtc,
      },
      members: [
        {
          firstName: firstName,
          lastName: lastName,
          middleName: null,
          dateOfBirth: moment(dob).format('YYYY-MM-DD'),
          sex: null,
          relationshipToSubscriber: null,
          address: null,
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
        },
      ],
      payer: {
        id: 'BS001',
        name: 'Blue Shield of California',
        address: null,
        code: 'BS001',
        fragmentUpdatedAt: nowUtc,
        fragmentSource: source,
      },
      planDetails: null,
      networkStatus: null,
      additionalBenefits: [
        {
          code: source,
          description: 'Invoiced Home visits with copay',
          type: 'VISIT_COPAY',
          level: 'FAMILY',
          serviceConstraints: {
            mode: 'INCLUDE',
            criteriaList: [
              {
                code: 'SICK_CHILD',
                level: 'FAMILY',
              },
              {
                code: 'SICK_ADULT',
                level: 'FAMILY',
              },
            ],
            fragmentUpdatedAt: nowUtc,
            fragmentSource: source,
          },
          beginDate: nowUtc,
          endDate: endUtc,
          credits: {
            total: 0,
            remaining: 0,
          },
          costEstimation: {
            copay: '15.00',
          },
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
          metadata: null,
        },
        {
          code: source,
          description: 'Invoiced TeleHealth visits with copay',
          type: 'VISIT_COPAY',
          level: 'FAMILY',
          serviceConstraints: {
            mode: 'INCLUDE',
            criteriaList: [
              {
                code: 'TELEMED_ADULT',
                level: 'FAMILY',
              },
              {
                code: 'TELEMED_CHILD',
                level: 'FAMILY',
              },
            ],
            fragmentUpdatedAt: nowUtc,
            fragmentSource: source,
          },
          beginDate: nowUtc,
          endDate: endUtc,
          credits: {
            total: 0,
            remaining: 0,
          },
          costEstimation: {
            copay: '5.00',
          },
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
          metadata: null,
        },
      ],
      reimbursement: {
        mode: 'INVOICE',
        fragmentUpdatedAt: nowUtc,
        fragmentSource: source,
      },
      tags: [
        {
          name: planSourceOptions.find((o) => o.key === source)?.text,
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
        },
      ],
      fragmentSources: [source],
    };

    return plan;
  }

  private generateBlueShieldMentalHealthPlan(firstName: string, lastName: string, memberId: string, dob: Date, source: string, nowUtc: string): any {
    const endUtc =
      moment(`01/01/${new Date().getFullYear() + 1}`)
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';

    const plan = {
      subscriberDetails: {
        memberId: memberId,
        fragmentSource: source,
        fragmentUpdatedAt: nowUtc,
      },
      members: [
        {
          firstName: firstName,
          lastName: lastName,
          middleName: null,
          dateOfBirth: moment(dob).format('YYYY-MM-DD'),
          sex: null,
          relationshipToSubscriber: null,
          address: null,
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
        },
      ],
      payer: {
        id: 'BS001',
        name: 'Blue Shield of California',
        address: null,
        code: 'BS001',
        fragmentUpdatedAt: nowUtc,
        fragmentSource: source,
      },
      planDetails: null,
      networkStatus: null,
      additionalBenefits: [
        {
          code: 'BSC_MENTAL_HEALTH',
          description: 'Free visits',
          type: 'VISIT_CREDIT',
          level: 'INDIVIDUAL',
          serviceConstraints: {
            mode: 'INCLUDE',
            criteriaList: [
              {
                code: 'THERAPY_ADULT',
                level: 'INDIVIDUAL',
              },
            ],
            fragmentUpdatedAt: nowUtc,
            fragmentSource: source,
          },
          beginDate: nowUtc,
          endDate: endUtc,
          credits: {
            total: 3,
            remaining: 3,
          },
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
          metadata: null,
        },
      ],
      reimbursement: {
        mode: 'INVOICE',
      },
      tags: [
        {
          name: planSourceOptions.find((o) => o.key === source)?.text,
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
        },
      ],
      fragmentSources: [source],
    };

    return plan;
  }

  private generateBlueShieldHRAPlan(firstName: string, lastName: string, memberId: string, dob: Date, source: string, nowUtc: string): any {
    const endUtc =
      moment(`01/01/${new Date().getFullYear() + 1}`)
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';

    const plan = {
      subscriberDetails: {
        memberId: memberId,
        fragmentSource: source,
        fragmentUpdatedAt: nowUtc,
      },
      members: [
        {
          firstName: firstName,
          lastName: lastName,
          middleName: null,
          dateOfBirth: moment(dob).format('YYYY-MM-DD'),
          sex: null,
          relationshipToSubscriber: null,
          address: null,
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
        },
      ],
      payer: {
        id: 'BS001',
        name: 'Blue Shield of California',
        address: null,
        code: 'BS001',
        fragmentUpdatedAt: nowUtc,
        fragmentSource: source,
      },
      planDetails: null,
      networkStatus: null,
      additionalBenefits: [
        {
          code: source,
          description: 'Free visits',
          type: 'VISIT_CREDIT',
          level: 'INDIVIDUAL',
          serviceConstraints: {
            mode: 'INCLUDE',
            criteriaList: [
              {
                code: 'ANPS_ADULT',
                level: 'INDIVIDUAL',
              },
              {
                code: 'TELEMED_ADULT',
                level: 'INDIVIDUAL',
              },
            ],
            fragmentUpdatedAt: nowUtc,
            fragmentSource: source,
          },
          beginDate: nowUtc,
          endDate: endUtc,
          credits: {
            total: 1,
            remaining: 1,
          },
          fragmentUpdatedAt: nowUtc,
          fragmentSource: source,
          metadata: null,
        },
      ],
      reimbursement: {
        mode: 'CLAIM',
      },
      tags: [],
      fragmentSources: [source],
    };

    return plan;
  }
}

function download(filename: string, text: string) {
  const element = document.createElement('a');
  element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
  element.setAttribute('download', filename);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}
